/**
 * useAttempts.js — small hook that owns the list of attempts for a paper,
 * the currently active attempt, and per-page autosave debouncing.
 *
 * Annotations are stored as { strokes: Stroke[] } per page (0-indexed).
 * A Stroke is { id, tool, color, size, points: [[x,y,pressure?]] } in PDF
 * user-space coordinates (i.e. the page width/height at PDF scale=1).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiJson } from '../api';

export function useAttempts({ paperId, token, enabled }) {
  const [attempts, setAttempts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [pages, setPages] = useState({}); // { [pageIndex]: { strokes: [...] } }
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | dirty | saving | saved | error

  const pendingSaves = useRef(new Map()); // pageIndex -> timer
  const inflight = useRef(new Set());

  // ── Load attempts list ────────────────────────────────────────────────
  const refreshAttempts = useCallback(async () => {
    if (!enabled || !paperId) return;
    try {
      const data = await apiJson(`/api/attempts?paperId=${encodeURIComponent(paperId)}`, { token });
      setAttempts(data.attempts || []);
      // Keep current active if still present, else clear
      setActiveId((prev) => (prev && data.attempts?.some((a) => a.id === prev) ? prev : null));
    } catch (err) {
      console.warn('Failed to load attempts:', err);
    }
  }, [paperId, token, enabled]);

  useEffect(() => { refreshAttempts(); }, [refreshAttempts]);

  // ── Load pages whenever active attempt changes ────────────────────────
  useEffect(() => {
    if (!activeId) { setPages({}); return; }
    let cancelled = false;
    setLoading(true);
    apiJson(`/api/attempts/${activeId}/pages`, { token })
      .then((data) => {
        if (cancelled) return;
        const map = {};
        for (const page of data.pages || []) {
          // Backend stores the strokes JSON column directly. Older rows wrote
          // `{ strokes: [...] }`; new rows store the array. Normalise both.
          const raw = page.strokes;
          const arr = Array.isArray(raw)
            ? raw
            : (Array.isArray(raw?.strokes) ? raw.strokes : []);
          map[page.pageIndex] = { strokes: arr };
        }
        setPages(map);
        setSaveStatus('idle');
      })
      .catch((err) => { if (!cancelled) console.warn('Failed to load pages:', err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeId, token]);

  // ── Create / select / rename / delete ────────────────────────────────
  const createAttempt = useCallback(async (name) => {
    const data = await apiJson('/api/attempts', {
      method: 'POST', token, body: { paperId, name },
    });
    setAttempts((prev) => [data.attempt, ...prev]);
    setActiveId(data.attempt.id);
    return data.attempt;
  }, [paperId, token]);

  const selectAttempt = useCallback((id) => setActiveId(id), []);

  const renameAttempt = useCallback(async (id, name) => {
    const data = await apiJson(`/api/attempts/${id}`, { method: 'PATCH', token, body: { name } });
    setAttempts((prev) => prev.map((a) => (a.id === id ? data.attempt : a)));
  }, [token]);

  const setAttemptScore = useCallback(async (id, { score, totalMarks }) => {
    const body = {};
    if (score !== undefined) body.score = score;
    if (totalMarks !== undefined) body.totalMarks = totalMarks;
    const data = await apiJson(`/api/attempts/${id}`, { method: 'PATCH', token, body });
    setAttempts((prev) => prev.map((a) => (a.id === id ? data.attempt : a)));
    return data.attempt;
  }, [token]);

  const deleteAttempt = useCallback(async (id) => {
    await apiJson(`/api/attempts/${id}`, { method: 'DELETE', token });
    setAttempts((prev) => prev.filter((a) => a.id !== id));
    setActiveId((prev) => (prev === id ? null : prev));
  }, [token]);

  // ── Autosave (debounced 800ms per page) ───────────────────────
  const flushPage = useCallback(async (pageIndex, payload) => {
    if (!activeId) return;
    const key = `${activeId}:${pageIndex}`;
    if (inflight.current.has(key)) return;
    inflight.current.add(key);
    setSaveStatus('saving');
    try {
      // Server expects { strokes: Array }. `payload` is { strokes: [...] }.
      const arr = Array.isArray(payload?.strokes) ? payload.strokes : [];
      await apiJson(`/api/attempts/${activeId}/pages/${pageIndex}`, {
        method: 'PUT', token, body: { strokes: arr },
      });
      setSaveStatus('saved');
    } catch (err) {
      console.warn('Save failed:', err);
      setSaveStatus('error');
    } finally {
      inflight.current.delete(key);
    }
  }, [activeId, token]);

  const updatePage = useCallback((pageIndex, updater) => {
    setPages((prev) => {
      const current = prev[pageIndex] || { strokes: [] };
      const next = typeof updater === 'function' ? updater(current) : updater;
      const result = { ...prev, [pageIndex]: next };
      // Schedule debounced save
      if (activeId) {
        setSaveStatus('dirty');
        const existing = pendingSaves.current.get(pageIndex);
        if (existing) clearTimeout(existing);
        const timer = setTimeout(() => {
          pendingSaves.current.delete(pageIndex);
          flushPage(pageIndex, next);
        }, 800);
        pendingSaves.current.set(pageIndex, timer);
      }
      return result;
    });
  }, [activeId, flushPage]);

  // Force flush all pending writes (used before submit)
  const flushAll = useCallback(async () => {
    const entries = Array.from(pendingSaves.current.entries());
    for (const [pageIndex, timer] of entries) {
      clearTimeout(timer);
      pendingSaves.current.delete(pageIndex);
      await flushPage(pageIndex, pages[pageIndex] || { strokes: [] });
    }
  }, [pages, flushPage]);

  // ── Timer control ──────────────────────────────────────────────────
  const timerAction = useCallback(async (id, action) => {
    const data = await apiJson(`/api/attempts/${id}/timer`, {
      method: 'PATCH', token, body: { action },
    });
    setAttempts((prev) => prev.map((a) => (a.id === id ? data.attempt : a)));
    return data.attempt;
  }, [token]);

  return {
    attempts, activeId, pages, loading, saveStatus,
    createAttempt, selectAttempt, renameAttempt, deleteAttempt,
    updatePage, flushAll, refreshAttempts, setActiveId,
    timerAction, setAttemptScore,
  };
}
